import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { submitContact } from "../lib/api";
import { useSettings } from "../lib/settings";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const s = settings;
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "General inquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Build map + directions URLs from the settings address so admin edits update the embed too.
  const { mapEmbedUrl, directionsUrl } = useMemo(() => {
    const fullAddress = `${s.address_line1 || ""}, ${s.address_line2 || ""}`.trim().replace(/^,\s*|,\s*$/g, "");
    const q = encodeURIComponent(fullAddress || "Nashville, TN");
    // Prefer the admin-provided Google Maps embed URL from Share > Embed a map.
    // Google's basic maps.google.com/maps?output=embed URL sends X-Frame-Options: SAMEORIGIN
    // and is blocked in ALL iframes on ALL domains, so we can't reliably auto-generate it.
    // The "pb=" URL from Google's Share menu is the only iframe-embeddable format
    // that works without a Maps Embed API key.
    let embed = s.map_embed_src && s.map_embed_src.trim() ? s.map_embed_src.trim() : null;
    if (!embed) {
      // Fallback: try the classic embed URL (works in some browsers/domains, may show
      // "refused to connect" if Google blocks it — admin should paste a real Share URL).
      embed = `https://maps.google.com/maps?q=${q}&t=m&z=15&ie=UTF8&iwloc=near&output=embed`;
    }
    return {
      mapEmbedUrl: embed,
      directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    };
  }, [s.address_line1, s.address_line2, s.map_embed_src]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll reply within one business day.");
      setForm({ full_name: "", email: "", phone: "", subject: "General inquiry", message: "" });
    } catch { toast.error("Send failed. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="page-contact" className="pt-32 pb-24 max-w-[1500px] mx-auto px-6 md:px-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="overline">Contact</div>
        <h1 className="font-display text-6xl md:text-8xl mt-3 tracking-tighter">
          Come by. Say hi.
          <br />
          <span className="text-[var(--primary)]">Take one home.</span>
        </h1>
      </motion.div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12">
        <div className="space-y-6">
          <Card icon={MapPin} title="Lot Address">
            <a data-testid="contact-address-link" href={s.google_maps_url} target="_blank" rel="noreferrer" className="hover:text-[var(--primary)]">
              {s.address_line1}<br />{s.address_line2}
            </a>
          </Card>
          <Card icon={Phone} title="Call"><a data-testid="contact-phone-link" href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="hover:text-[var(--primary)]">{s.phone}</a></Card>
          <Card icon={Mail} title="Email"><a data-testid="contact-email-link" href={`mailto:${s.email}`} className="hover:text-[var(--primary)]">{s.email}</a></Card>
          <Card icon={Clock} title="Hours">
            {s.hours_weekday}<br />
            {s.hours_sunday}
          </Card>
        </div>

        <form onSubmit={onSubmit} className="glass rounded-2xl p-8 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label>Full name</label>
              <input required data-testid="c-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <label>Email</label>
              <input required type="email" data-testid="c-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label>Phone</label>
              <input data-testid="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label>Subject</label>
              <select data-testid="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
                <option>General inquiry</option>
                <option>Vehicle question</option>
                <option>Book a visit</option>
                <option>Financing</option>
                <option>Trade-in</option>
              </select>
            </div>
          </div>
          <div>
            <label>Message</label>
            <textarea required rows={5} data-testid="c-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button disabled={submitting} type="submit" className="btn-primary w-full justify-center" data-testid="c-submit">
            {submitting ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>

      {/* Google Maps embed */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="mt-24"
        data-testid="contact-map-section"
      >
        <div className="mb-6">
          <div className="overline flex items-center gap-2"><Navigation size={12} /> Find Us</div>
          <h2 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">
            On the map.
          </h2>
          <p className="text-white/60 mt-3 text-sm max-w-md">
            {s.address_line1}, {s.address_line2}
          </p>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-white/10 glass" data-testid="contact-map-embed">
          {/* Dark overlay ring for cinematic frame */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 z-10" />
          <iframe
            title="Map to Pick Auto Sales"
            src={mapEmbedUrl}
            width="100%"
            height="480"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="block w-full h-[420px] md:h-[480px] border-0"
            style={{ filter: "invert(90%) hue-rotate(180deg) saturate(0.85) contrast(0.95)" }}
          />
          <a
            href={directionsUrl}
            target="_top"
            rel="noopener noreferrer"
            className="absolute bottom-4 right-4 z-20 text-xs px-3 py-1.5 rounded-full bg-black/80 border border-white/20 backdrop-blur text-white/90 hover:bg-[var(--primary)] hover:border-[var(--primary)] transition-colors"
            data-testid="contact-map-open"
          >
            Open in Google Maps →
          </a>
        </div>
      </motion.section>
    </div>
  );
}

const Card = ({ icon: Icon, title, children }) => (
  <div className="bento-cell">
    <Icon size={16} className="text-[var(--primary)]" />
    <div className="overline mt-3">{title}</div>
    <div className="mt-2 text-white/80 leading-relaxed">{children}</div>
  </div>
);
